import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Alert,
  Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/common/Card';
import { Input } from '../../components/common/Input';
import { Button } from '../../components/common/Button';
import { Logo } from '../../components/layout/Logo';
import { setSelectedOrganization } from '../../utils/auth';
import { auth } from '../../services/api'; 
import { useAuth } from '../../contexts/AuthContext';

const RegisterOrg: React.FC = () => {
  const [orgId, setOrgId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!orgId.trim()) {
      setError('Organization ID is required');
      return;
    }

    try {
      const { data } = await auth.validateOrganization(orgId);
      if (data.valid) {
        setSelectedOrganization(data.organization);
        navigate('/register');
      } else {
        setError(data.error || 'Invalid Organization ID');
      }
    } catch (err: any) {
      console.error('Organization validation error:', err);
      setError(err.response?.data?.error || 'Failed to validate organization. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        position: 'relative',
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            <Logo style={{ height: '80px', width: 'auto' }} />
          </Box>

          <Card
            sx={{
              width: '100%',
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: 'rgba(38, 38, 38, 0.6)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                mb: 4,
                color: 'primary.main',
                textAlign: 'center',
              }}
            >
              Enter Organization ID
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              <Input
                required
                fullWidth
                id="orgId"
                label="Organization ID"
                name="orgId"
                autoComplete="off"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Continue
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link 
                  href="/login" 
                  variant="body2"
                  sx={{ 
                    cursor: 'pointer',
                    color: 'text.secondary',
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main'
                    }
                  }}
                >
                  {"Already have an account? Sign In"}
                </Link>
              </Box>
            </Box>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default RegisterOrg;